import { create } from 'zustand'

import type { SystemMetrics } from '@/api/types'

/**
 * What the health page has seen of the host, one point per poll.
 *
 * The server keeps no history, so this is the browser's own record: taken only
 * while the page is open, kept for an hour, and mirrored into `localStorage` so
 * a reload or a detour to another admin page does not start the charts over.
 */
const HISTORY_KEY = 'lithos.admin.healthHistory'

/** How far back the record reaches. */
export const HISTORY_MS = 60 * 60 * 1000

export interface Sample {
  /** When the server took it, in epoch milliseconds. */
  at: number
  cpu: number
  memory: number
  /** Worker slots running a task, and the slots there are. */
  busy: number
  capacity: number
  /** Tasks waiting in every queue. */
  queued: number
}

export function sampleOf(metrics: SystemMetrics): Sample {
  const { workers, queues } = metrics.workers
  return {
    at: Date.parse(metrics.sampled_at),
    cpu: metrics.cpu.percent,
    memory: metrics.memory.percent,
    busy: workers.reduce((sum, worker) => sum + worker.busy, 0),
    capacity: workers.reduce((sum, worker) => sum + worker.concurrency, 0),
    queued: queues.reduce((sum, queue) => sum + queue.size, 0),
  }
}

/** The record with one more point, and without whatever has aged out of it. */
export function appended(history: readonly Sample[], sample: Sample): Sample[] {
  const since = sample.at - HISTORY_MS
  return [...history, sample]
    .filter((held) => held.at >= since)
    .sort((a, b) => a.at - b.at)
}

/** A point with no values, which a line chart draws as a break. */
export type Gap = Pick<Sample, 'at'> & Partial<Omit<Sample, 'at'>>

/**
 * The record with a break wherever the page was not watching.
 *
 * Without one a line runs straight across the hour the tab was closed, which
 * reads as an hour of steady load that nobody measured.
 */
export function withGaps(history: readonly Sample[], interval: number): (Sample | Gap)[] {
  const drawn: (Sample | Gap)[] = []
  history.forEach((sample, index) => {
    const previous = history[index - 1]
    if (previous !== undefined && sample.at - previous.at > interval * 3) {
      drawn.push({ at: previous.at + interval })
    }
    drawn.push(sample)
  })
  return drawn
}

function isSample(value: unknown): value is Sample {
  if (typeof value !== 'object' || value === null) return false
  const held = value as Record<string, unknown>
  return ['at', 'cpu', 'memory', 'busy', 'capacity', 'queued'].every(
    (key) => typeof held[key] === 'number',
  )
}

function readHistory(): Sample[] {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]')
    // Anything else was written by another version of this page, or by hand.
    return Array.isArray(raw) ? raw.filter(isSample) : []
  } catch {
    // Storage throws outright in a few configurations; a chart that starts
    // empty on every visit beats a page that will not render.
    return []
  }
}

function writeHistory(samples: readonly Sample[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(samples))
  } catch {
    // As above.
  }
}

interface HealthHistory {
  samples: Sample[]
  record: (sample: Sample) => void
}

export const useHealthHistory = create<HealthHistory>((set, get) => ({
  samples: readHistory(),

  record: (sample) => {
    // One answer read twice -- a remount, a refetch the server answered from
    // the same instant -- is one point.
    if (get().samples.some((held) => held.at === sample.at)) return
    const samples = appended(get().samples, sample)
    writeHistory(samples)
    set({ samples })
  },
}))
